import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ArrowLeft, HelpCircle, BookMarked, FileText, Users, Settings, CreditCard, Mail } from 'lucide-react';

const FAQ = [
  { q: 'What is dsashun?', a: 'dsashun is an AI-powered platform that helps pastors, teachers, and ministry leaders create professional Bible studies, devotionals, sermon notes, and curriculum in minutes.' },
  { q: 'Is this tool meant to replace personal study?', a: 'Absolutely not. This tool is designed to assist — never to replace — diligent, prayer-filled, and Spirit-led preparation. It provides a starting framework that you should always refine through personal study and prayer.' },
  { q: 'How does the free trial work?', a: 'New users get a 7-day Pro trial with unlimited access to all features. After the trial, you can continue on the Free plan (5 generations/month) or upgrade to a paid plan.' },
  { q: 'Can I export my content?', a: 'Yes! Depending on your plan, you can export to PDF, Word (.docx), and PowerPoint (.pptx) formats — all with your church branding.' },
  { q: 'How does the Sermon Planner work?', a: 'The Sermon Planner lets you create quarter, half-year, or full-year sermon series plans. AI generates thematic arcs, scripture selections, and weekly outlines that you can then refine.' },
  { q: 'Can I manage multiple users?', a: 'Yes, the Church plan includes an Admin Panel where you can create and manage user accounts, set subscription tiers, and toggle features on/off.' },
  { q: 'What languages are supported?', a: 'Currently 7 languages: English, Spanish, French, Portuguese, German, Korean, and Chinese.' },
  { q: 'How do I cancel my subscription?', a: 'You can manage or cancel your subscription anytime from the Pricing page in your dashboard. No long-term commitments.' },
];

export default function SupportPage() {
  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="support-page">
      <header className="bg-white border-b border-[#E2DFD8]">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <span className="text-lg font-bold text-[#3D3229] tracking-wide uppercase" style={{fontFamily:"'Nunito', 'Quicksand', sans-serif", letterSpacing:'0.12em'}}>dsashun</span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-sm text-[#475569] hover:text-[#1B3A6B]">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#0F1A2A] mb-4" data-testid="support-title">Support Center</h1>
          <p className="text-lg text-[#475569]">Find answers to common questions or reach out to our team.</p>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {[
            { icon: BookMarked, label: 'Getting Started', desc: 'New user guide' },
            { icon: CreditCard, label: 'Billing', desc: 'Plans & payments' },
            { icon: Settings, label: 'Account', desc: 'Settings & profile' },
            { icon: Mail, label: 'Contact', desc: 'Reach our team', to: '/contact' },
          ].map((item, i) => (
            item.to ? (
              <Link key={i} to={item.to} className="bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow text-center">
                <item.icon className="w-6 h-6 text-[#1B3A6B] mx-auto mb-2" strokeWidth={1.5} />
                <h3 className="font-medium text-sm text-[#0F1A2A]">{item.label}</h3>
                <p className="text-xs text-[#475569] mt-1">{item.desc}</p>
              </Link>
            ) : (
              <div key={i} className="bg-white rounded-xl p-5 shadow-sm text-center">
                <item.icon className="w-6 h-6 text-[#1B3A6B] mx-auto mb-2" strokeWidth={1.5} />
                <h3 className="font-medium text-sm text-[#0F1A2A]">{item.label}</h3>
                <p className="text-xs text-[#475569] mt-1">{item.desc}</p>
              </div>
            )
          ))}
        </div>

        {/* FAQ */}
        <h2 className="font-heading text-2xl font-bold text-[#1B3A6B] mb-6">Frequently Asked Questions</h2>
        <div className="space-y-4" data-testid="faq-list">
          {FAQ.map((item, i) => (
            <details key={i} className="bg-white rounded-xl shadow-sm group">
              <summary className="px-6 py-4 cursor-pointer font-medium text-[#0F1A2A] flex items-center justify-between hover:text-[#1B3A6B] list-none">
                <span className="flex items-center gap-3">
                  <HelpCircle className="w-5 h-5 text-[#B8860B] flex-shrink-0" strokeWidth={1.5} />
                  {item.q}
                </span>
                <span className="text-[#475569] group-open:rotate-180 transition-transform">&#9662;</span>
              </summary>
              <div className="px-6 pb-4 text-[#475569] text-sm leading-relaxed pl-14">{item.a}</div>
            </details>
          ))}
        </div>

        {/* Still need help */}
        <div className="mt-12 bg-[#1B3A6B] rounded-2xl p-8 text-center">
          <h3 className="font-heading text-xl font-bold text-white mb-2">Still Need Help?</h3>
          <p className="text-white/70 mb-4">Our team is here to support your ministry.</p>
          <Link to="/contact" className="inline-block px-6 py-3 bg-[#B8860B] text-white rounded-lg font-medium hover:bg-[#D19C11] transition-colors" data-testid="support-contact-btn">
            Contact Support
          </Link>
        </div>
      </div>

      <footer className="bg-[#0F1A2A] text-white/60 py-8 px-6 mt-8">
        <div className="max-w-6xl mx-auto text-center text-sm">
          &copy; {new Date().getFullYear()} dsashun. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
